import type { SxProps, Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { alpha } from "@mui/material/styles";
import {
  dashboardCardSx,
  dashboardInteractiveFocusSx,
  TOP_LIST_ITEM_WIDTH_PX,
  topListKpiGridItemSx
} from "../Dashboard/dashboard.styles";
import {
  dashboard2DayHeaderTextSx,
  dashboard2SectionLabelBaseSx
} from "./dashboard2-typography.styles";

/** 12px corners (3 × default theme.shape.borderRadius). */
const dashboard2SectionBorderRadius = 3;

export const dashboard2SectionSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboardCardSx(theme),
  borderRadius: dashboard2SectionBorderRadius,
  alignItems: "stretch",
  width: "100%"
});

export const dashboard2TodayStripSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  width: "100%"
};

export const dashboard2TodayBookingRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  width: "100%",
  minWidth: 0
};

export const dashboard2TodayBookingThumbnailSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: 48,
  height: 48,
  borderRadius: 1,
  overflow: "hidden",
  bgcolor: "action.hover"
};

export const dashboard2TodayBookingDetailsSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.25,
  minWidth: 0,
  flex: 1
};

export const dashboard2TodayColleaguesSectionSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  width: "100%",
  minWidth: 0
};

export const dashboard2TodayColleaguesHeaderSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  minWidth: 0,
  width: "100%"
};

export const dashboard2TodayColleagueRowSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.25,
  width: "100%",
  minWidth: 0
};

/** Avatar diameter for colleague chips and the narrow colleague row slot. */
export const DASHBOARD2_CHIP_AVATAR_SIZE_PX = 20;

/**
 * Colleague entries shown before the narrow top-row list scrolls.
 * Wide rows are capped by the top-grid height instead.
 */
export const DASHBOARD2_TODAY_COLLEAGUES_NARROW_VISIBLE_ROWS = 4;

/** Name line. The avatar is the tallest item on that line. */
const DASHBOARD2_TODAY_COLLEAGUE_IDENTITY_LINE_PX = DASHBOARD2_CHIP_AVATAR_SIZE_PX;

/** `dashboard2TodayColleagueRowSx` gap 0.25 at the default 8px spacing scale. */
const DASHBOARD2_TODAY_COLLEAGUE_ROW_GAP_PX = 2;

/** Caption line box (MUI caption 0.75rem × line-height 1.66 at 16px root). */
const DASHBOARD2_TODAY_COLLEAGUE_PLACE_LINE_PX = 20;

/** Spare pixels so the fourth row is not clipped by sub-pixel line boxes. */
const DASHBOARD2_TODAY_COLLEAGUE_NARROW_ROW_SLACK_PX = 2;

/**
 * Fixed slot for one colleague on the narrow list (name line, gap, place line).
 * Short entries use the same slot so a fifth person always starts below the fold.
 */
export const DASHBOARD2_TODAY_COLLEAGUE_NARROW_ROW_HEIGHT_PX =
  DASHBOARD2_TODAY_COLLEAGUE_IDENTITY_LINE_PX +
  DASHBOARD2_TODAY_COLLEAGUE_ROW_GAP_PX +
  DASHBOARD2_TODAY_COLLEAGUE_PLACE_LINE_PX +
  DASHBOARD2_TODAY_COLLEAGUE_NARROW_ROW_SLACK_PX;

/** Matches `dashboard2TodayStripSx` `gap: 1`. */
const DASHBOARD2_TODAY_COLLEAGUES_STRIP_GAP_SPACING = 1;

export const dashboard2TodayColleagueNarrowRowSx: SxProps<Theme> = {
  ...dashboard2TodayColleagueRowSx,
  boxSizing: "border-box",
  height: DASHBOARD2_TODAY_COLLEAGUE_NARROW_ROW_HEIGHT_PX,
  flexShrink: 0,
  overflow: "hidden"
};

/** Scrollport max height for exactly four narrow colleague slots, including the strip gaps. */
export const dashboard2TodayColleaguesNarrowMaxHeight = (stripGapCss: string): string =>
  `calc(${DASHBOARD2_TODAY_COLLEAGUES_NARROW_VISIBLE_ROWS} * ${DASHBOARD2_TODAY_COLLEAGUE_NARROW_ROW_HEIGHT_PX}px + ${
    DASHBOARD2_TODAY_COLLEAGUES_NARROW_VISIBLE_ROWS - 1
  } * ${stripGapCss})`;

export const dashboard2TodayColleagueIdentityRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  minWidth: 0,
  width: "100%"
};

export const dashboard2TodayColleagueNameGroupSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  minWidth: 0,
  flex: 1
};

export const dashboard2TodayColleagueBookingRowSx = (
  avatarSizePx: number,
  gapPx: number
): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "column",
  gap: 0.25,
  minWidth: 0,
  pl: `${avatarSizePx + gapPx}px`
});

/**
 * Colleagues section stack. Hugs the list. On a wide row it cannot grow past the top-row height.
 */
export const dashboard2TodayColleaguesSectionRootSx = (
  isWideRow: boolean
): SystemStyleObject<Theme> => ({
  alignSelf: "flex-start",
  height: "auto",
  maxHeight: isWideRow ? "100%" : "none",
  minHeight: 0,
  ...(isWideRow ? { overflow: "hidden" } : {})
});

/**
 * Colleagues card body. Stays as tall as the list.
 * On a wide row it can shrink so a long list scrolls inside the top-row height.
 */
export const dashboard2TodayColleaguesPanelSx = (
  theme: Theme,
  isWideRow: boolean
): SxProps<Theme> => ({
  ...dashboard2TodayFieldsetSx(theme),
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  minHeight: 0,
  height: "auto",
  ...(isWideRow ? { flex: "0 1 auto", overflow: "hidden" } : {})
});

/** List scroller: wide rows shrink inside the top row; narrow rows stop after four colleagues. */
export const dashboard2TodayColleaguesScrollSx = (isWideRow: boolean): SxProps<Theme> =>
  isWideRow
    ? {
        minWidth: 0,
        minHeight: 0,
        flex: "0 1 auto",
        overflowY: "auto",
        overscrollBehavior: "contain"
      }
    : theme => ({
        minWidth: 0,
        overflowY: "auto",
        overscrollBehavior: "contain",
        maxHeight: dashboard2TodayColleaguesNarrowMaxHeight(
          theme.spacing(DASHBOARD2_TODAY_COLLEAGUES_STRIP_GAP_SPACING)
        )
      });

/** “Now” band — tier-1 primary wash (uniform border). */
export const dashboard2TodayFieldsetSx = (theme: Theme): SxProps<Theme> => ({
  border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
  borderRadius: dashboard2SectionBorderRadius,
  margin: 0,
  p: 1.5,
  width: "100%",
  height: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.12 : 0.04),
  transition: theme.transitions.create(["border-color", "background-color"], {
    duration: theme.transitions.duration.short
  }),
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none"
  }
});

export const dashboard2TodayLegendSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboard2SectionLabelBaseSx,
  color: theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.main
});

export const dashboard2TodayDateSx: SxProps<Theme> = {
  fontSize: "0.875rem",
  fontWeight: 600,
  lineHeight: 1.35,
  color: "text.primary"
};

/** Compact vertical list on wide top row; wrapped layouts use touch-friendly height. */
export const DASHBOARD2_QUICK_ACTION_BUTTON_MIN_HEIGHT_COMPACT_PX = 34;
export const DASHBOARD2_QUICK_ACTION_BUTTON_MIN_HEIGHT_TOUCH_PX = 44;

export const dashboard2QuickActionsSx = (isWideRow: boolean): SxProps<Theme> => ({
  display: "flex",
  flexDirection: isWideRow ? "column" : "row",
  flexWrap: "wrap",
  alignItems: "stretch",
  gap: isWideRow ? 0.75 : 1,
  width: "100%"
});

/** Action-list buttons: icon leading, label left-aligned, full width when stacked. */
export const dashboard2QuickActionButtonSx = (isWideRow: boolean): SxProps<Theme> => ({
  justifyContent: "flex-start",
  textAlign: "left",
  px: 1.25,
  py: 0.375,
  minHeight: isWideRow
    ? DASHBOARD2_QUICK_ACTION_BUTTON_MIN_HEIGHT_COMPACT_PX
    : DASHBOARD2_QUICK_ACTION_BUTTON_MIN_HEIGHT_TOUCH_PX,
  "@media (pointer: coarse)": {
    minHeight: DASHBOARD2_QUICK_ACTION_BUTTON_MIN_HEIGHT_TOUCH_PX,
    py: 0.5
  },
  fontSize: "0.8125rem",
  lineHeight: 1.25,
  flex: isWideRow ? "0 0 auto" : { xs: "1 1 calc(50% - 4px)", sm: "1 1 180px" },
  width: isWideRow ? "100%" : { xs: "calc(50% - 4px)", sm: "auto" },
  maxWidth: isWideRow ? "100%" : { xs: "calc(50% - 4px)", sm: "none" },
  "& .MuiButton-startIcon": {
    marginLeft: 0,
    marginRight: 0.75,
    "& .MuiSvgIcon-root": {
      fontSize: "1.125rem"
    }
  },
  "& .MuiButton-startIcon svg path": {
    fill: "currentColor"
  }
});

/** Secondary booking actions — outlined, subordinate to planning controls. */
export const dashboard2QuietQuickActionButtonSx = (
  theme: Theme,
  isWideRow: boolean
): SxProps<Theme> => ({
  ...(dashboard2QuickActionButtonSx(isWideRow) as object),
  color: theme.palette.text.primary,
  borderColor: theme.palette.divider,
  "&:hover": {
    borderColor: theme.palette.text.secondary,
    bgcolor: theme.palette.action.hover
  }
});

export const dashboard2EmbeddedSectionSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboard2SectionSx(theme),
  mb: 0,
  height: "100%"
});

/** Neutral action panel — buttons carry emphasis, not the surface. */
export const dashboard2EmbeddedQuickActionsSectionSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboard2EmbeddedSectionSx(theme),
  bgcolor: theme.palette.background.paper,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: theme.palette.background.paper
  }
});

export const dashboard2WeekHeroSectionSx = (theme: Theme): SxProps<Theme> => ({
  ...dashboard2SectionSx(theme),
  p: 2,
  bgcolor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  "&:hover": {
    backgroundColor: theme.palette.background.paper
  }
});

/** Embedded calendar / table — natural height; page scrolls via app-parent. */
export const dashboard2ScheduleEmbedSx = (theme: Theme): SxProps<Theme> => ({
  borderRadius: dashboard2SectionBorderRadius,
  flexShrink: 0,
  overflow: "visible",
  width: "100%",
  bgcolor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  "& .mbsc-eventcalendar": {
    borderRadius: "inherit"
  }
});

export const dashboard2TodayQuickActionsRowSx: SxProps<Theme> = {
  width: "100%",
  alignItems: "flex-start",
  justifyContent: "flex-start"
};

/** Below the wide top-row breakpoint: cards fill their grid column without max-width caps. */
export const dashboard2TopRowStackedCellContentSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  minWidth: 0,
  "& > *": {
    width: "100%",
    boxSizing: "border-box"
  }
};

export const dashboard2TodayQuickActionsCellSx: SxProps<Theme> = {
  display: "flex",
  minWidth: 0,
  height: "100%"
};

export const dashboard2ScheduleLayoutToggleGroupSx = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";

  return {
    flexShrink: 0,
    borderRadius: 2,
    overflow: "hidden",
    border: 1,
    borderColor: "divider",
    bgcolor: "background.paper",
    "& .MuiToggleButton-root": {
      px: 1.5,
      py: 1,
      border: 0,
      borderRadius: 0,
      ...(isDark ? { color: theme.palette.text.secondary } : {})
    },
    ...(isDark
      ? {
          // Doubled .Mui-selected selector raises specificity above MUI dark-mode defaults without !important.
          "& .MuiToggleButton-root.MuiToggleButton-primary.Mui-selected.Mui-selected": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white
            },
            "& .MuiSvgIcon-root, & .MuiTypography-root": {
              color: "inherit"
            }
          }
        }
      : {})
  };
};

export const dashboard2WeekNavSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  my: { xs: 0.5, md: 1 }
};

export const dashboard2WeekGridSx = (isMobile: boolean): SxProps<Theme> => ({
  display: "grid",
  gridTemplateColumns: isMobile ? "1fr" : "repeat(5, minmax(0, 1fr))",
  gap: 1,
  width: "100%"
});

export const dashboard2DayColumnSx = (
  theme: Theme,
  isToday = false,
  interactive = false
): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  const defaultColumnBg = isDark ? alpha(theme.palette.common.white, 0.04) : theme.palette.grey[50];

  return {
    border: `1px solid ${
      isToday ? alpha(theme.palette.primary.main, 0.35) : theme.palette.divider
    }`,
    borderRadius: 1,
    p: 1,
    minHeight: 120,
    display: "flex",
    flexDirection: "column",
    gap: 0.75,
    bgcolor: isToday ? alpha(theme.palette.primary.main, isDark ? 0.14 : 0.06) : defaultColumnBg,
    ...(interactive
      ? {
          cursor: "pointer",
          transition: theme.transitions.create(["background-color", "box-shadow"], {
            duration: theme.transitions.duration.short
          }),
          "&:hover": {
            bgcolor: isToday
              ? alpha(theme.palette.primary.main, isDark ? 0.2 : 0.1)
              : theme.palette.action.hover,
            boxShadow: theme.shadows[1]
          },
          ...dashboardInteractiveFocusSx
        }
      : {})
  };
};

export const dashboard2DayHeaderSx = (theme: Theme, isToday = false): SxProps<Theme> =>
  dashboard2DayHeaderTextSx(theme, isToday);

export const dashboard2UserSectionSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.5
};

export const dashboard2BookingChipIconSlotSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: DASHBOARD2_CHIP_AVATAR_SIZE_PX,
  height: DASHBOARD2_CHIP_AVATAR_SIZE_PX,
  flexShrink: 0
};

export const dashboard2BookingChipDetailsRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: 0.75,
  width: "100%",
  minWidth: 0
};

export const dashboard2BookingChipUserRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  width: "100%",
  minWidth: 0,
  mb: 0.5
};

export const dashboard2BookingChipSx = (
  theme: Theme,
  accentColor: string,
  hasUserHeader = false
): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  const chipBg = isDark ? alpha(theme.palette.common.white, 0.06) : alpha(accentColor, 0.1);
  const chipBgHover = isDark ? alpha(theme.palette.common.white, 0.1) : alpha(accentColor, 0.16);

  return {
    display: "flex",
    flexDirection: hasUserHeader ? "column" : "row",
    alignItems: hasUserHeader ? "stretch" : "flex-start",
    gap: hasUserHeader ? 0 : 0.75,
    px: 1,
    py: 0.75,
    borderRadius: 1.5,
    width: "100%",
    boxSizing: "border-box",
    textAlign: "left",
    color: "text.primary",
    border: `1px solid ${alpha(accentColor, isDark ? 0.45 : 0.35)}`,
    bgcolor: chipBg,
    transition: theme.transitions.create(["box-shadow", "background-color"], {
      duration: theme.transitions.duration.short
    }),
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover": {
        transform: "none"
      }
    },
    "&:hover": {
      bgcolor: chipBgHover,
      boxShadow: theme.shadows[2]
    },
    ...dashboardInteractiveFocusSx
  };
};

export const dashboard2TopLocationsRowSx: SxProps<Theme> = {
  display: "flex",
  gap: 2,
  overflowX: "auto",
  pb: 0.5,
  width: "100%",
  flexWrap: "nowrap"
};

/** Same fixed width as dashboard TopList tiles — do not grow in the scroll row. */
export const dashboard2TopListItemSx: SxProps<Theme> = {
  ...topListKpiGridItemSx,
  flex: `0 0 ${TOP_LIST_ITEM_WIDTH_PX}px`,
  width: TOP_LIST_ITEM_WIDTH_PX,
  maxWidth: TOP_LIST_ITEM_WIDTH_PX,
  minWidth: TOP_LIST_ITEM_WIDTH_PX
};

export const dashboard2WfhChipSx = (color: string): SxProps<Theme> => ({
  display: "inline-flex",
  alignItems: "center",
  px: 1.5,
  py: 0.75,
  borderRadius: 2,
  bgcolor: "action.hover",
  color,
  fontWeight: 600,
  fontSize: "0.875rem"
});

// ---------------------------------------------------------------------------
// Top-row layout constants
// See docs/features/dashboard/FE/top-row-responsive-grid-plan.md
// ---------------------------------------------------------------------------

/** Flex-basis / min width (px) for the Today card on the wide top row (not the interior grid). */
export const DASHBOARD2_TODAY_MAIN_MIN_WIDTH_PX = 450;

/** Minimum width (px) reserved for Today's contextual side column in wide mode. */
export const DASHBOARD2_TODAY_SIDE_MIN_WIDTH_PX = 220;

/** Gap (px) between Today main and side columns in the wide interior grid (`theme.spacing(2)`). */
export const DASHBOARD2_TODAY_WIDE_SIDE_ROW_GAP_PX = 16;

/**
 * Minimum width (px) for the Today main stack (date, headline, check-in) before showing a side column.
 * Used only for fit calculations — not the top-row flex basis.
 */
export const DASHBOARD2_TODAY_WIDE_MAIN_STACK_MIN_WIDTH_PX = 300;

/**
 * Reserved width (px) for the soft hint side column (copy max 180px + padding/border).
 * Aligns with `dashboard2TodaySideHintSx` / `dashboard2TodaySideEmptySx`.
 */
export const DASHBOARD2_TODAY_WIDE_HINT_SIDE_MIN_WIDTH_PX = 200;

/** Hysteresis (px) when hiding the side column after it was shown (reduces boundary flicker). */
export const DASHBOARD2_TODAY_WIDE_SIDE_HIDE_HYSTERESIS_PX = 20;

/** Derived min Today section width (px) to show the soft wide-side hint (main + hint side + gap). */
export const DASHBOARD2_TODAY_WIDE_HINT_FIT_MIN_WIDTH_PX =
  DASHBOARD2_TODAY_WIDE_MAIN_STACK_MIN_WIDTH_PX +
  DASHBOARD2_TODAY_WIDE_HINT_SIDE_MIN_WIDTH_PX +
  DASHBOARD2_TODAY_WIDE_SIDE_ROW_GAP_PX;

/** Derived min Today section width (px) to show inline additional bookings (main + side + gap). */
export const DASHBOARD2_TODAY_SIDE_BOOKINGS_FIT_MIN_WIDTH_PX =
  DASHBOARD2_TODAY_WIDE_MAIN_STACK_MIN_WIDTH_PX +
  DASHBOARD2_TODAY_SIDE_MIN_WIDTH_PX +
  DASHBOARD2_TODAY_WIDE_SIDE_ROW_GAP_PX;

const _todayWideSideRowGapPx = DASHBOARD2_TODAY_WIDE_SIDE_ROW_GAP_PX;

/** @deprecated Use `DASHBOARD2_TODAY_WIDE_HINT_FIT_MIN_WIDTH_PX`. */
export const DASHBOARD2_TODAY_WIDE_HINT_MIN_WIDTH_PX = DASHBOARD2_TODAY_WIDE_HINT_FIT_MIN_WIDTH_PX;

/** @deprecated Use `DASHBOARD2_TODAY_SIDE_BOOKINGS_FIT_MIN_WIDTH_PX`. */
export const DASHBOARD2_TODAY_SIDE_BOOKINGS_MIN_WIDTH_PX =
  DASHBOARD2_TODAY_SIDE_BOOKINGS_FIT_MIN_WIDTH_PX;

/** Flex-basis/min width (px) of the Today card on the wide top row (main stack only). */
export const DASHBOARD2_TOP_ROW_TODAY_FLEX_BASIS_PX = DASHBOARD2_TODAY_MAIN_MIN_WIDTH_PX;

/** Min Today card width (px) for the full two-column wide interior (main + side + gap). */
export const DASHBOARD2_TOP_ROW_TODAY_WIDE_INTERIOR_MIN_WIDTH_PX =
  DASHBOARD2_TODAY_MAIN_MIN_WIDTH_PX + DASHBOARD2_TODAY_SIDE_MIN_WIDTH_PX + _todayWideSideRowGapPx;

/** @deprecated Use `DASHBOARD2_TOP_ROW_TODAY_WIDE_INTERIOR_MIN_WIDTH_PX` for interior grid mins. */
export const DASHBOARD2_TOP_ROW_TODAY_BASIS_WIDTH_PX =
  DASHBOARD2_TOP_ROW_TODAY_WIDE_INTERIOR_MIN_WIDTH_PX;

/** Max width (px) of the Colleagues card in the dashboard top row. */
export const DASHBOARD2_TOP_ROW_COLLEAGUES_MAX_WIDTH_PX = 460;

/** Fixed width (px) of the Favorite Location card (carousel reuse; not shown on dashboard top row). */
export const DASHBOARD2_TOP_ROW_FAVORITE_WIDTH_PX = 250;

/** Fixed width (px) of the Quick Actions card in the dashboard top row. */
export const DASHBOARD2_TOP_ROW_ACTIONS_WIDTH_PX = 280;

// Breakpoint derivation intermediates (not exported — change the source constants above).
const _topRowGapPx = 1.5 * 8; // Grid2 spacing=1.5, MUI default 8px scale → 12px/gap
const _topRowGapTotalPx = 2 * _topRowGapPx; // 2 gaps in a 3-item row → 24px

/**
 * Custom breakpoint (px) above which the top row renders as a single row.
 * Today starts from its min width, then grows to absorb all remaining row space.
 */
export const DASHBOARD2_TOP_ROW_BREAKPOINT_PX =
  DASHBOARD2_TOP_ROW_TODAY_FLEX_BASIS_PX +
  DASHBOARD2_TOP_ROW_COLLEAGUES_MAX_WIDTH_PX +
  DASHBOARD2_TOP_ROW_ACTIONS_WIDTH_PX +
  _topRowGapTotalPx +
  40;
